<template>
  <div class="reference-fields">
    <p class="muted">以下是运营参考资料，保存到 CMS，不下发规则、文案或奖励；实际效果以客户端发版为准。</p>
    <el-form label-width="100px" :disabled="disabled">
      <el-form-item label="分组">
        <el-select v-model="draft.display_group" clearable placeholder="选择分组">
          <el-option v-for="(label, key) in achievementGroups" :key="key" :label="label" :value="key" />
        </el-select>
      </el-form-item>
      <el-form-item label="默认名称"><el-input v-model="draft.name" placeholder="未填写翻译时显示的名称，可留空沿用运营名称" /></el-form-item>
      <el-form-item label="条件说明"><el-input v-model="draft.description" type="textarea" placeholder="例如：完成霓虹仙境合集中的 20 张拼图" /></el-form-item>
      <el-collapse>
        <el-collapse-item title="多语言名称与条件说明" name="translations">
          <div v-for="(label, language) in languages" :key="language" class="translation">
            <strong>{{ label }}</strong>
            <el-input v-model="draft.i18n_name[language]" :aria-label="`${label}名称`" placeholder="名称（可选）" />
            <el-input v-model="draft.i18n_desc[language]" :aria-label="`${label}条件说明`" type="textarea" placeholder="条件说明（可选）" />
          </div>
        </el-collapse-item>
      </el-collapse>
      <h3>阶段与奖励</h3>
      <p v-if="!draft.levels.length" class="muted">暂无阶段资料，可按客户端设计添加。</p>
      <el-collapse>
        <el-collapse-item v-for="(level, index) in draft.levels" :key="level.level_id || index" :name="index" :title="`第 ${index + 1} 阶段 · ${level.i18n_desc.ZH_HANS || level.description || '待填写条件'}`">
          <el-form-item label="默认条件"><el-input v-model="level.description" type="textarea" placeholder="此阶段的默认条件说明" /></el-form-item>
          <el-form-item label="目标数量"><el-input-number v-model="level.target" :min="0" :max="1e15" /></el-form-item>
          <el-form-item label="进度文案"><el-input v-model="level.progress_text" placeholder="例如：完成图片 0/20（可选）" /></el-form-item>
          <div v-for="(reward, rewardIndex) in level.reward_items" :key="rewardIndex" class="reward-editor">
            <el-select v-model="reward.type" aria-label="奖励类型" placeholder="奖励类型" @change="setRewardType(reward)">
              <el-option v-for="(label, type) in rewardTypes" :key="type" :label="label" :value="type" />
              <el-option v-if="reward.type && !rewardTypes[reward.type]" :label="reward.type" :value="reward.type" />
            </el-select>
            <el-select v-if="reward.type === 'TOOL'" v-model="reward.item_id" aria-label="道具" placeholder="选择道具">
              <el-option label="提示道具" value="achievement_hint" /><el-option label="拼图分区道具" value="achievement_partition" />
              <el-option v-if="reward.item_id && !['achievement_hint', 'achievement_partition'].includes(reward.item_id)" :label="reward.item_id" :value="reward.item_id" />
            </el-select>
            <template v-if="durationTypes.includes(reward.type)">
              <span>分钟</span><el-input-number :model-value="reward.duration_ms / 60000" :min="1" aria-label="奖励分钟数" @update:model-value="reward.duration_ms = $event * 60000" />
            </template>
            <template v-else><span>数量</span><el-input-number v-model="reward.quantity" :min="1" :precision="0" aria-label="奖励数量" /></template>
            <el-button type="danger" plain @click="level.reward_items.splice(rewardIndex, 1)">移除奖励</el-button>
          </div>
          <p v-if="level.reward_items.some(reward => reward.type === 'IMAGE')" class="muted">保存资料后，可在“图片与名称”中选择奖励拼图。已有图片绑定会保留。</p>
          <el-button @click="level.reward_items.push({type: 'COIN', quantity: 1})">添加奖励</el-button>
          <el-collapse class="stage-translations">
            <el-collapse-item title="阶段多语言文案" name="stage-copy">
              <div v-for="(label, language) in languages" :key="language" class="translation">
                <strong>{{ label }}</strong>
                <el-input v-model="level.i18n_desc[language]" :aria-label="`${label}阶段条件`" type="textarea" placeholder="阶段条件说明（可选）" />
                <el-input v-model="level.i18n_progress[language]" :aria-label="`${label}阶段进度文案`" placeholder="进度文案（可选）" />
              </div>
            </el-collapse-item>
          </el-collapse>
          <el-button type="danger" plain @click="draft.levels.splice(index, 1)">移除此阶段资料</el-button>
        </el-collapse-item>
      </el-collapse>
      <el-button class="add-stage" @click="draft.levels.push({target: 1, description: '', i18n_desc: {}, i18n_progress: {}, reward_items: []})">添加阶段</el-button>
    </el-form>
  </div>
</template>

<script>
import {achievementGroups, referenceLanguages, durationRewardTypes} from '../libs/achievementReference.mjs';

export default {
  name: 'AchievementReferenceFields',
  // 父页面传入独立草稿，所有字段只修改草稿；点击保存后才调用资料接口。
  props: {draft: {type: Object, required: true}, disabled: Boolean},
  data() {
    return {
      achievementGroups,
      rewardTypes: {COIN: '金币', TOOL: '道具', IMAGE: '奖励拼图', NO_ADS: '免横幅广告', WATERMARK: '免水印', UNLIMITED_HINTS: '无限提示', UNLIMITED_JIGSORT: '无限分区'},
      durationTypes: durationRewardTypes,
    };
  },
  computed: {
    languages() {
      const custom = new Set([...Object.keys(this.draft.i18n_name), ...Object.keys(this.draft.i18n_desc),
        ...this.draft.levels.flatMap(level => [...Object.keys(level.i18n_desc), ...Object.keys(level.i18n_progress)])]);
      return {...Object.fromEntries([...custom].map(key => [key, key])), ...referenceLanguages};
    },
  },
  methods: {
    setRewardType(reward) {
      if (!this.durationTypes.includes(reward.type)) reward.quantity ||= 1;
      if (reward.type === 'TOOL') reward.item_id ||= 'achievement_hint';
      if (this.durationTypes.includes(reward.type)) reward.duration_ms ||= 60000;
    },
  },
};
</script>

<style scoped>
.muted { color: var(--el-text-color-secondary); font-size: 13px; }
.translation { display: grid; grid-template-columns: 90px 1fr 2fr; gap: 10px; margin: 12px 0; align-items: start; }
.reward-editor { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin: 12px 0; }
.reward-editor .el-select { width: 160px; }
.stage-translations, .add-stage { margin: 16px 0; }
</style>
